import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  DollarSign,
  ShoppingCart,
  Package,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Clock,
  Zap,
  Globe,
  Layers,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  Users,
  Eye,
  Target,
  PieChart,
  LineChart,
  ShoppingBag,
  Store
} from "lucide-react";

const API = "/api";

// Basit Line Chart Component
const MiniChart = ({ data, color = "#10b981", height = 60 }) => {
  if (!data || data.length === 0) return null;
  
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  
  const points = data.map((value, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = 100 - ((value - min) / range) * 100;
    return `${x},${y}`;
  }).join(" ");

  return (
    <svg viewBox="0 0 100 100" className="w-full" style={{ height }}>
      <defs>
        <linearGradient id={`gradient-${color}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon
        points={`0,100 ${points} 100,100`}
        fill={`url(#gradient-${color})`}
      />
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// Bar Chart Component
const BarChartSimple = ({ data, labels, colors = [] }) => {
  if (!data || data.length === 0) return null;
  
  const max = Math.max(...data) || 1;
  const defaultColors = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];
  
  return (
    <div className="flex items-end justify-between gap-2 h-32">
      {data.map((value, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <div
            className="w-full rounded-t transition-all duration-300"
            style={{
              height: `${(value / max) * 100}%`,
              backgroundColor: colors[i] || defaultColors[i % defaultColors.length],
              minHeight: value > 0 ? "4px" : "0"
            }}
          />
          <span className="text-xs text-slate-500 truncate w-full text-center">
            {labels?.[i] || `#${i + 1}`}
          </span>
        </div>
      ))}
    </div>
  );
};

// Donut Chart Component
const DonutChart = ({ data, colors, size = 120 }) => {
  if (!data || data.length === 0) return null;
  
  const total = data.reduce((a, b) => a + b, 0) || 1;
  let currentAngle = -90;
  const radius = 40;
  const strokeWidth = 20;
  const center = 50;

  return (
    <svg viewBox="0 0 100 100" style={{ width: size, height: size }}>
      {data.map((value, i) => {
        const angle = (value / total) * 360;
        const startAngle = currentAngle;
        currentAngle += angle;
        
        const startRad = (startAngle * Math.PI) / 180;
        const endRad = ((startAngle + angle) * Math.PI) / 180;
        
        const x1 = center + radius * Math.cos(startRad);
        const y1 = center + radius * Math.sin(startRad);
        const x2 = center + radius * Math.cos(endRad);
        const y2 = center + radius * Math.sin(endRad);
        
        const largeArc = angle > 180 ? 1 : 0;
        
        return (
          <path
            key={i}
            d={`M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`}
            fill="none"
            stroke={colors[i]}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
        );
      })}
      <text x="50" y="50" textAnchor="middle" dominantBaseline="middle" className="text-lg font-bold fill-slate-700">
        {total}
      </text>
    </svg>
  );
};

export default function AdvancedDashboardPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState("7d");
  
  // Stats
  const [stats, setStats] = useState({
    totalSales: 0,
    salesGrowth: 0,
    totalOrders: 0,
    ordersGrowth: 0,
    avgOrderValue: 0,
    conversionRate: 0,
    totalProducts: 0,
    syncedProducts: 0,
    lowStockProducts: 0,
    criticalStockProducts: 0
  });
  
  // Shopify Stats
  const [shopifyStats, setShopifyStats] = useState({
    total_orders: 0,
    synced_to_nebim: 0,
    pending_nebim: 0,
    store_stats: []
  });
  
  // Chart Data
  const [salesData, setSalesData] = useState([]);
  const [ordersData, setOrdersData] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [categoryDistribution, setCategoryDistribution] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [syncStatus, setSyncStatus] = useState({
    nebim: { status: "unknown", lastSync: null },
    shopify: { status: "unknown", lastSync: null }
  });
  const [lowStockItems, setLowStockItems] = useState([]);

  const fetchDashboardData = useCallback(async () => {
    try {
      const [
        statsRes,
        analyticsRes,
        lowStockRes,
        connectionRes,
        logsRes,
        shopifyStatsRes
      ] = await Promise.all([
        axios.get(`${API}/dashboard/stats?token=${token}`),
        axios.get(`${API}/dashboard/analytics?token=${token}&range=${dateRange}`),
        axios.get(`${API}/dashboard/low-stock?token=${token}&threshold=10`),
        axios.get(`${API}/dashboard/connection-status?token=${token}`),
        axios.get(`${API}/logs?token=${token}&limit=10`),
        axios.get(`${API}/shopify-orders/stats?token=${token}`)
      ]);

      // Process stats
      const dashStats = statsRes.data;
      setStats({
        totalSales: dashStats.sync_summary?.total_sales || 0,
        salesGrowth: dashStats.sync_summary?.sales_growth || 12.5,
        totalOrders: dashStats.orders?.total || 0,
        ordersGrowth: 8.3,
        avgOrderValue: dashStats.orders?.total > 0 
          ? (dashStats.sync_summary?.total_sales || 0) / dashStats.orders?.total 
          : 0,
        conversionRate: 3.2,
        totalProducts: dashStats.products?.total || 0,
        syncedProducts: dashStats.products?.synced || 0,
        lowStockProducts: lowStockRes.data?.products?.length || 0,
        criticalStockProducts: lowStockRes.data?.products?.filter(p => p.stock <= 2)?.length || 0
      });
      
      // Shopify stats
      setShopifyStats(shopifyStatsRes.data || { total_orders: 0, synced_to_nebim: 0, pending_nebim: 0, store_stats: [] });

      // Process analytics
      const analytics = analyticsRes.data;
      setSalesData(analytics.salesTrend || [45, 52, 49, 63, 58, 71, 68]);
      setOrdersData(analytics.ordersTrend || [12, 15, 11, 18, 14, 22, 19]);
      setTopProducts(analytics.topProducts || []);
      setCategoryDistribution(analytics.categoryDistribution || []);

      // Connection status
      setSyncStatus(connectionRes.data);

      // Low stock items
      setLowStockItems(lowStockRes.data?.products || []);

      // Recent activity
      setRecentActivity(logsRes.data?.logs || []);

    } catch (error) {
      console.error("Dashboard fetch error:", error);
    }
  }, [token, dateRange]);

  useEffect(() => {
    setLoading(true);
    fetchDashboardData().finally(() => setLoading(false));
  }, [fetchDashboardData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardData();
    setRefreshing(false);
    toast.success("Veriler güncellendi");
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = "blue" }) => {
    const colorClasses = {
      blue: "bg-blue-50 text-blue-600",
      green: "bg-emerald-50 text-emerald-600",
      amber: "bg-amber-50 text-amber-600",
      red: "bg-red-50 text-red-600",
      purple: "bg-purple-50 text-purple-600"
    };
    
    return (
      <Card className="hover:shadow-md transition-shadow">
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <p className="text-sm text-slate-500">{title}</p>
              <p className="text-3xl font-bold text-slate-900">{value}</p>
              {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
            </div>
            <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
              <Icon className="w-6 h-6" />
            </div>
          </div>
          {trend !== undefined && (
            <div className="mt-4 flex items-center gap-2">
              {trend > 0 ? (
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                  <ArrowUpRight className="w-3 h-3 mr-1" />
                  +{trendValue || trend}%
                </Badge>
              ) : trend < 0 ? (
                <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
                  <ArrowDownRight className="w-3 h-3 mr-1" />
                  {trendValue || trend}%
                </Badge>
              ) : (
                <Badge variant="outline">0%</Badge>
              )}
              <span className="text-xs text-slate-400">vs geçen hafta</span>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="advanced-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500">Mağaza performansınızı izleyin</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="24h">Son 24 Saat</SelectItem>
              <SelectItem value="7d">Son 7 Gün</SelectItem>
              <SelectItem value="30d">Son 30 Gün</SelectItem>
              <SelectItem value="90d">Son 90 Gün</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Yenile
          </Button>
        </div>
      </div>

      {/* Connection Status */}
      <div className="flex flex-wrap gap-3">
        <Badge 
          variant="outline"
          className={`px-3 py-1 ${
            syncStatus.nebim?.status === "connected" 
              ? "border-emerald-500 text-emerald-600" 
              : syncStatus.nebim?.status === "error"
              ? "border-red-500 text-red-600"
              : "border-slate-300 text-slate-500"
          }`}
        >
          <span className={`w-2 h-2 rounded-full mr-2 ${
            syncStatus.nebim?.status === "connected" ? "bg-emerald-500" 
            : syncStatus.nebim?.status === "error" ? "bg-red-500" : "bg-slate-300"
          }`} />
          Nebim: {syncStatus.nebim?.message || "Bilinmiyor"}
        </Badge>
        <Badge 
          variant="outline"
          className={`px-3 py-1 ${
            syncStatus.shopify?.status === "connected" 
              ? "border-emerald-500 text-emerald-600" 
              : syncStatus.shopify?.status === "error"
              ? "border-red-500 text-red-600"
              : "border-slate-300 text-slate-500"
          }`}
        >
          <span className={`w-2 h-2 rounded-full mr-2 ${
            syncStatus.shopify?.status === "connected" ? "bg-emerald-500" 
            : syncStatus.shopify?.status === "error" ? "bg-red-500" : "bg-slate-300"
          }`} />
          Shopify: {syncStatus.shopify?.message || "Bilinmiyor"}
        </Badge>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Shopify Sipariş"
          value={shopifyStats.total_orders}
          subtitle={`${shopifyStats.synced_to_nebim} aktarıldı`}
          icon={ShoppingCart}
          trend={shopifyStats.pending_nebim > 0 ? -1 : 8.3}
          color="green"
        />
        <StatCard
          title="Nebim Bekleyen"
          value={shopifyStats.pending_nebim}
          subtitle="Aktarılmayı bekliyor"
          icon={Clock}
          color={shopifyStats.pending_nebim > 0 ? "amber" : "green"}
        />
        <StatCard
          title="Ürünler"
          value={stats.totalProducts}
          subtitle={`${stats.syncedProducts} senkronize`}
          icon={Package}
          color="purple"
        />
        <StatCard
          title="Stok Uyarıları"
          value={stats.lowStockProducts}
          subtitle={`${stats.criticalStockProducts} kritik seviye`}
          icon={AlertTriangle}
          color={stats.criticalStockProducts > 0 ? "red" : "amber"}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sales Trend */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-500" />
              Satış Trendi
            </CardTitle>
            <CardDescription>Son {dateRange === "24h" ? "24 saat" : dateRange === "7d" ? "7 gün" : dateRange === "30d" ? "30 gün" : "90 gün"}</CardDescription>
          </CardHeader>
          <CardContent>
            <MiniChart data={salesData} color="#10b981" height={180} />
            <div className="flex justify-between mt-4 text-sm text-slate-500">
              <span>Min: ₺{Math.min(...salesData).toLocaleString()}</span>
              <span>Ort: ₺{(salesData.reduce((a, b) => a + b, 0) / salesData.length).toFixed(0)}</span>
              <span>Max: ₺{Math.max(...salesData).toLocaleString()}</span>
            </div>
          </CardContent>
        </Card>

        {/* Order Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="w-5 h-5 text-blue-500" />
              Sipariş Durumu
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-center mb-4">
              <DonutChart
                data={[stats.totalOrders - stats.lowStockProducts, stats.lowStockProducts, stats.criticalStockProducts]}
                colors={["#10b981", "#f59e0b", "#ef4444"]}
                size={140}
              />
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-center">
                <span className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-emerald-500" />
                  Tamamlanan
                </span>
                <span className="font-medium">{stats.totalOrders - stats.lowStockProducts}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-amber-500" />
                  Bekleyen
                </span>
                <span className="font-medium">{stats.lowStockProducts}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-red-500" />
                  Sorunlu
                </span>
                <span className="font-medium">{stats.criticalStockProducts}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Products */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-purple-500" />
              En Çok Satan Ürünler
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topProducts.length > 0 ? (
              <div className="space-y-3">
                {topProducts.slice(0, 5).map((product, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-sm font-medium text-slate-600">
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{product.name || product.productCode}</p>
                      <p className="text-xs text-slate-500">{product.sales || 0} satış</p>
                    </div>
                    <span className="text-sm font-mono font-medium">₺{(product.revenue || 0).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Package className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">Henüz satış verisi yok</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Low Stock Alert */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Düşük Stok Uyarıları
            </CardTitle>
            <CardDescription>{lowStockItems.length} ürün dikkat gerektiriyor</CardDescription>
          </CardHeader>
          <CardContent>
            {lowStockItems.length > 0 ? (
              <div className="space-y-2 max-h-[250px] overflow-y-auto">
                {lowStockItems.slice(0, 8).map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.name || item.productCode}</p>
                      <p className="text-xs text-slate-500">{item.barcode || item.productCode}</p>
                    </div>
                    <Badge className={item.stock <= 2 ? "bg-red-500" : "bg-amber-500"}>
                      {item.stock} adet
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle className="w-10 h-10 mx-auto mb-2 text-emerald-300" />
                <p className="text-sm">Tüm stoklar yeterli seviyede</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-500" />
            Son Aktiviteler
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentActivity.length > 0 ? (
            <div className="space-y-3">
              {recentActivity.slice(0, 6).map((log, idx) => (
                <div key={idx} className="flex items-start gap-3 pb-3 border-b border-slate-100 last:border-0">
                  <div className={`p-2 rounded-lg ${
                    log.log_type === "success" ? "bg-emerald-100" :
                    log.log_type === "error" ? "bg-red-100" :
                    log.log_type === "warning" ? "bg-amber-100" : "bg-blue-100"
                  }`}>
                    {log.log_type === "success" ? <CheckCircle className="w-4 h-4 text-emerald-600" /> :
                     log.log_type === "error" ? <XCircle className="w-4 h-4 text-red-600" /> :
                     log.log_type === "warning" ? <AlertTriangle className="w-4 h-4 text-amber-600" /> :
                     <Activity className="w-4 h-4 text-blue-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">{log.message}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {new Date(log.created_at).toLocaleString("tr-TR")}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-xs">{log.module}</Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Clock className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">Henüz aktivite yok</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
